import { apiClient } from "@leap4/frontend";
import { useEffect, useState } from "react";
import { predictionBaseUrl } from "../../../../config/generals";
import type { IPredictionSeriesPoint } from "./reportChart";

// No dimensions means the campaign total. Cohort-level series exist, but a chart of every cohort is
// unreadable, and the grid is where individual cohorts are read.

/** One event's series, kept apart so the chart can colour and label each event separately. */
export interface IPredictionEventSeries {
    event: string;
    points: IPredictionSeriesPoint[];
}

/**
 * Fetches a prediction series per selected event. Separate from the report request because the
 * forecast half covers dates that have no impressions yet, so no report row exists for them.
 */
export interface IPredictionSeriesState {
    series: IPredictionEventSeries[];
    /** Set when a fetch failed, so the chart can say so instead of looking empty. */
    error: string | null;
}

export const usePredictionSeries = (
    idCampaign: string | undefined,
    events: string[],
    /** Bump to refetch, e.g. from a Retry button. */
    reloadKey = 0,
): IPredictionSeriesState => {
    const [series, setSeries] = useState<IPredictionEventSeries[]>([]);
    const [error, setError] = useState<string | null>(null);
    // Which request the state belongs to, so a scope change never shows the previous campaign's series.
    const [loadedKey, setLoadedKey] = useState("");
    const key = `${idCampaign ?? ""}|${events.join(",")}|${reloadKey}`;

    useEffect(() => {
        let cancelled = false;
        if (events.length === 0) {
            setSeries([]);
            setError(null);
            return undefined;
        }
        // Said out loud rather than returning nothing: a prediction is fitted per campaign, and a
        // silent empty series is indistinguishable from one that came back with no data.
        if (!idCampaign || idCampaign === "all") {
            setSeries([]);
            setError("pick a single campaign — predictions are fitted per campaign");
            setLoadedKey(key);
            return undefined;
        }

        const load = async () => {
            // Settled per event: one event failing leaves the rest drawn rather than blanking the chart.
            const results = await Promise.allSettled(
                events.map((event) => {
                    const params = new URLSearchParams({ id_campaign: idCampaign, event });
                    return apiClient.get<{ windows: unknown | null; points: IPredictionSeriesPoint[] }>(
                        `${predictionBaseUrl}/series?${params.toString()}`,
                    );
                }),
            );
            if (cancelled) return;

            const loaded: IPredictionEventSeries[] = [];
            const failed: string[] = [];
            results.forEach((result, index) => {
                if (result.status === "fulfilled") {
                    loaded.push({ event: events[index], points: result.value?.data?.points ?? [] });
                } else {
                    failed.push(events[index]);
                }
            });

            setSeries(loaded);
            setLoadedKey(key);
            // windows is null only when the feature is switched off. Without saying so, a disabled
            // killswitch and a campaign with nothing to predict render identically — an empty chart.
            const allDisabled =
                results.length > 0
                && results.every(
                    (r) => r.status === "fulfilled" && r.value?.data?.windows === null,
                );
            if (allDisabled) {
                setSeries([]);
                setError("Predictions are turned off for this environment.");
                return;
            }
            // Surfaced rather than swallowed: an empty chart and a failed request look identical, and
            // the grid keeps working either way, so silence here costs debugging time for no benefit.
            setError(failed.length ? `Prediction series unavailable for ${failed.join(", ")}` : null);
        };
        void load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // Do not expose a previous event/campaign's series while the empty selection effect settles.
    return events.length === 0 ? { series: [], error: null }
        : loadedKey === key ? { series, error } : { series: [], error: null };
};
